import { useEffect, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import { storage } from '../features/user/userManager';
import { useAuth } from 'react-oidc-context';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  fetchFilterByMotionType,
  resultFilters,
  sectionFilters,
  selectedFilters,
  updateFilter,
  filtersState,
} from '../features/filter/filterSlice';
import { fetchTranslations } from '../features/translation/slice';
import { fetchAuthKeys } from '../features/auth/slice';
import { listOfMotionType, updateValuesFromUrl, isLoginRedirect } from '../utils';
import type { MotionTypeOptions } from '../interfaces';
import useTracking from '../hooks/useTracking';

export default function useInit(): void {
  const [searchParams] = useSearchParams();
  const { backendPrefix: prefix } = useAppSelector((state) => state.base);
  const { pathname } = useLocation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { movingMass } = useAppSelector(selectedFilters);
  const filterList = useAppSelector(sectionFilters);
  const resultsFilterList = useAppSelector(resultFilters);

  const { filters: storedFilters } = useAppSelector((state) => state.filters);
  const { motionType } = storedFilters;
  const auth = useAuth();
  const { isAuthenticated: userIsAuthenticated } = auth;

  useTracking();

  const fetchFiltersData = () => {
    const fetchFilters = async (motionType: MotionTypeOptions) => await dispatch(fetchFilterByMotionType(motionType));
    let routeMotionType = pathname.split('/')[1];

    const isResultsPage = pathname.includes('results');

    if (
      (routeMotionType && isResultsPage) ||
      (listOfMotionType.verticalmotionfreefallingmass !== listOfMotionType[routeMotionType] && !isResultsPage)
    ) {
      routeMotionType = routeMotionType.replaceAll('-', '').replaceAll('_', '');
      const motionType = listOfMotionType[routeMotionType];
      if (motionType) {
        fetchFilters(motionType);
      }
    }

    dispatch(
      updateFilter({
        name: 'motionType',
        newValue: listOfMotionType[routeMotionType],
      }),
    );
  };

  const updateFilters = () => {
    const isResultsPage = pathname.includes('results');

    if (movingMass === 0 && isResultsPage) {
      const filterKeys = Object.keys(filtersState);
      const filterValuesByUrl = filterKeys.map((key) => searchParams.get(key));

      filterKeys.forEach((filter, index) => {
        const value = filterValuesByUrl[index];

        if (value === null) return {};

        const updatedValue = updateValuesFromUrl({ key: filter as keyof typeof filtersState, value: value });

        dispatch(
          updateFilter({
            name: updatedValue.name,
            newValue: updatedValue.newValue,
          }),
        );
      });
    }
  };

  useEffect(() => {
    const preAuthState = storage.getItem('preAuthState');

    if (isLoginRedirect() && preAuthState !== null) {
      dispatch(fetchFilterByMotionType(motionType)).catch((error) => console.error(error));
    }

    if (preAuthState !== null && preAuthState !== window.location.hash) {
      // Redirect to the saved state or default route
      window.location.hash = preAuthState;
    }
    if (userIsAuthenticated) storage.removeItem('preAuthState');
  }, []);

  useEffect(() => {
    if (userIsAuthenticated) return;

    if (!isLoginRedirect()) {
      storage.setItem('preAuthState', window.location.hash);
    }
  }, [userIsAuthenticated, location]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      auth.signinSilent();
    }
  }, []);
  // Begin authentication workaround logic

  useEffect(() => {
    dispatch(fetchTranslations());
  }, [dispatch, prefix]);

  useEffect(() => {
    dispatch(fetchAuthKeys(prefix));
  }, [dispatch, prefix]);

  // end logic workaround login
  useLayoutEffect(() => {
    fetchFiltersData();
  }, []);

  useLayoutEffect(() => {
    updateFilters();
  }, [dispatch, filterList, movingMass, pathname, resultsFilterList, searchParams]);
}
